import React, {
  useMemo,
  useCallback,
  useEffect,
  useState,
  useRef,
} from "react";
import "./ViewFiltersPanel.scss";
// import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import useScreenSize from "../../shared/useScreenSize";
// import { clearFilters } from "../../shared/filterUtlis";
import { Tooltip, Button, Menu } from "cprime-foundational-components";

interface FilterChip {
  title: string;
  value: string;
  id: number;
}

interface ViewFiltersPanelProps {
  filterValues?: FilterChip[];
  setFilterValues: (values: FilterChip[]) => void;
}

const ViewFiltersPanel: React.FC<ViewFiltersPanelProps> = ({
  filterValues = [],
  setFilterValues,
}) => {
  const [hiddenFilters, setHiddenFilters] = useState<any[]>([]);
  const [visibleFiltersCount, setVisibleFiltersCount] = useState(0);

  // Type the refs for the DOM elements
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chipsContainerRef = useRef<HTMLDivElement | null>(null);
  const clearButtonRef = useRef<HTMLDivElement | null>(null);

  const screenSize = useScreenSize();

  // All potential filters
  const allFilters = useMemo(() => {
    return filterValues.map((f, i) => ({
      title: f.title,
      value: f.value,
      priority: i + 1,
    }));
  }, [filterValues]);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleOn = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const calculateVisibleFilters = useCallback(() => {
    if (
      !containerRef.current ||
      !chipsContainerRef.current ||
      !clearButtonRef.current
    ) {
      return;
    }

    const containerWidth = containerRef.current.offsetWidth;
    const clearButtonWidth = clearButtonRef.current.offsetWidth;
    const infoIconAndLabelWidth = 150; // Approximate width of info icon + "View Filtered by:" text
    const moreButtonWidth = 80; // Approximate width for "+X More" button
    const chipMargin = 8; // Margin between chips

    // Available space for filter chips
    const availableWidth =
      containerWidth -
      clearButtonWidth -
      infoIconAndLabelWidth -
      moreButtonWidth;

    // Create temporary div to measure filter chip widths
    const tempDiv = document.createElement("div");
    tempDiv.style.position = "absolute";
    tempDiv.style.visibility = "hidden";
    tempDiv.className = "filterChip";
    document.body.appendChild(tempDiv);

    // Measure each filter's width
    const filterWidths = allFilters.map((filter) => {
      tempDiv.innerHTML = `<span class="filter-view-filter-title">${filter.title}:</span>
                           <span class="filter-view-filter-value">${filter.value}</span>`;
      return tempDiv.offsetWidth + chipMargin;
    });

    document.body.removeChild(tempDiv);

    // Determine how many filters we can show
    let currentWidth = 0;
    let visibleCount = 0;

    for (let i = 0; i < allFilters.length; i++) {
      if (currentWidth + filterWidths[i] <= availableWidth) {
        currentWidth += filterWidths[i];
        visibleCount++;
      } else {
        break;
      }
    }

    // If we have at least one filter to show and we can't show all filters,
    // reserve space for the "+X More" button
    if (visibleCount > 0 && visibleCount < allFilters.length) {
      // Re-check if we can fit all visible filters plus the "More" button
      while (
        visibleCount > 0 &&
        currentWidth + moreButtonWidth > availableWidth
      ) {
        visibleCount--;
        currentWidth -= filterWidths[visibleCount];
      }
    }

    // Sort filters by priority before deciding which to show
    const sortedFilters = [...allFilters].sort(
      (a, b) => a.priority - b.priority,
    );

    // Get the visible filters based on priority
    // const visibleFilters = sortedFilters.slice(0, visibleCount);
    const newHiddenFilters = sortedFilters.slice(visibleCount);

    setVisibleFiltersCount(visibleCount);
    setHiddenFilters(newHiddenFilters);
  }, [allFilters]);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(calculateVisibleFilters);
    const container = containerRef.current;
    if (container) {
      resizeObserver.observe(container);
    }
    return () => {
      if (container) {
        resizeObserver.unobserve(container);
      }
      resizeObserver.disconnect();
    };
  }, [allFilters, screenSize.width, calculateVisibleFilters]);

  // Sort filters by priority (highest priority first)
  const visibleFilters = allFilters
    .sort((a, b) => a.priority - b.priority)
    .slice(0, visibleFiltersCount);

  return (
    <div
      className="filter-view-container"
      ref={containerRef}
      style={{ display: "flex" }}
    >
      <div
        className="filter-content"
        ref={chipsContainerRef}
        style={{
          display: "flex",
          alignItems: "center",
          // flexGrow: 1,
          overflow: "hidden",
          whiteSpace: "nowrap",
        }}
      >
        {/* <InfoOutlinedIcon
          sx={{
            height: "16px",
            width: "16px",
            color: "rgba(88, 89, 91, 1)",
            flexShrink: 0,
          }}
        /> */}
        <div className="viewFilteredBy" style={{ flexShrink: 0 }}>
          View Filtered by:{" "}
        </div>

        {visibleFilters.map((filter, index) => (
          <div key={index} className="filterChip" style={{ flexShrink: 0 }}>
            <span className="filter-view-filter-title">{filter.title}:</span>
            <span className="filter-view-filter-value">{filter.value}</span>
          </div>
        ))}

        {hiddenFilters.length > 0 && (
          <div
            className="more"
            onClick={handleOn}
            style={{ flexShrink: 0 }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ")
                handleOn(e as unknown as React.MouseEvent<HTMLElement>);
            }}
          >
            <span>+{hiddenFilters.length} More</span>
          </div>
        )}

        {/* <Menu
          id="demo-customized-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            "aria-labelledby": "demo-customized-button",
            sx: {
              padding: 0,
            },
          }}
        >
          {hiddenFilters.map((filter, index) => (
            <MenuItem key={index} onClick={handleClose}>
              <div className="moreMenuCss">
                <span className="filter-view-filter-title">
                  {filter.title}:
                </span>
                <span className="filter-view-filter-value">{filter.value}</span>
              </div>
            </MenuItem>
          ))}
        </Menu> */}

        <Menu
          id="demo-customized-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          narrow
          groups={[
            {
              items: hiddenFilters.map((filter) => ({
                label: (
                  <div className="moreMenuCss">
                    <span className="filter-view-filter-title">
                      {filter.title}:
                    </span>{" "}
                    <span className="filter-view-filter-value">
                      {filter.value}
                    </span>
                  </div>
                ),
                onClick: handleClose,
              })),
            },
          ]}
        />
      </div>

      <div ref={clearButtonRef} style={{ flexShrink: 0 }}>
        <Tooltip
          title={<div>Removes all view filters</div>}
          disabled={false}
          placement="bottom"
        >
          <Button
            variant="ghost"
            size="small"
            onClick={() => {
              setFilterValues([]);
            }}
            disabled={filterValues.length === 0}
            sx={{
              border: "1px solid #000000",
              color: "#000000",
              textAlign: "center",
              fontSize: "14px",
              fontWeight: "400",
              lineHeight: "16px",
              cursor: "pointer",
              whiteSpace: "nowrap",
              height: "24px",
              padding: "8px 16px",
              gap: "10px",
            }}
          >
            Clear View Filters
          </Button>
        </Tooltip>
      </div>
    </div>
  );
};

export default ViewFiltersPanel;
